import React, { useState, useEffect } from "react";
import PopUpScreen from "../../component/popUpScreen/popUpScreen";
import Select from "react-select";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { LabelWrapper } from "../../component/inputtext/inputtextStyle";
import { FieldLabel } from "../global/global.style";
import { Message } from "primereact/message";
import { useQuery } from "@tanstack/react-query";
import accessManagementService from "../../services/accessManagementService";
import { CreateUserMutation } from "../../mutations/AccessManagementMutationHook";
import { useToast } from "../../context/ToastContext";
import { GetDomainAndSubdomains } from "../../queries/appQueries";

function AddNewMember({ onHide }) {
  // Form state
  const [email, setEmail] = useState("");
  const [parentDomain, setParentDomain] = useState([]);
  const [products, setProducts] = useState([]);
  const [domain, setDomain] = useState([]);
  const [subDomain, setSubDomain] = useState([]);
  const [cluster, setCluster] = useState(null);
  const [market, setMarket] = useState(null);
  const [role, setRole] = useState(null);
  const [errors, setErrors] = useState({});

  // Data fetching
  const { data: parentDomainData = {}, isLoading: isParentDomainLoading } = GetDomainAndSubdomains();
  const { data: countryAndRegion = {}, isLoading: isCountryAndRegionsLoading } = useQuery({
    queryKey: ["getCountryAndRegions"],
    queryFn: () => accessManagementService.getCountryAndRegion(),
  });
  const { data: allRoles = [], isLoading: isAllRolesDataLoading } = useQuery({
    queryKey: ["getAllUserRoles"],
    queryFn: () => accessManagementService.getAllRoles(),
  });
  const { data: productsData = [], isLoading: isProductsLoading } = useQuery({
    queryKey: ["getProducts"],
    queryFn: () => accessManagementService.getProducts(),
  });
  const rolesToDisplay = Array.isArray(allRoles) ? allRoles : [];
  const toast = useToast();
  const { mutate: createUser, isPending: isUserCreating } = CreateUserMutation(
    () => {
      toast({ severity: "info", summary: "Confirmed", detail: `User Created Successfullly`, life: 3000 });
      onHide();
    },
    (error) => {
      toast({ severity: "info", summary: "Error", detail: error?.response?.data?.detail || error.message, life: 3000 });
    }
  );

  // Helper: get all domains from selected parent domains
  const getAvailableDomains = (selectedParentDomains) => {
    if (!selectedParentDomains || selectedParentDomains.length === 0) return [];
    const allDomains = [];
    selectedParentDomains.forEach(parentDomain => {
      if (parentDomain.domains) {
        allDomains.push(...parentDomain.domains);
      }
    });
    return allDomains;
  };
  // Helper: get all subdomains from selected domains
  const getAvailableSubdomains = (selectedDomains) => {
    if (!selectedDomains || selectedDomains.length === 0) return [];
    const allSubdomains = [];
    selectedDomains.forEach(domain => {
      if (domain.sub_domains) {
        allSubdomains.push(...domain.sub_domains);
      }
    });
    return allSubdomains;
  };

  // Cluster and Market options
  const clusterOptions = Array.isArray(countryAndRegion)
    ? countryAndRegion.filter((obj, index, self) =>
        index === self.findIndex((item) => item.region === obj.region)
      ).map(item => ({ label: item.region, value: item.region }))
    : [];

  const getAvailableMarkets = (selectedRegion) => {
    if (!selectedRegion) return [];
    const allMarkets = countryAndRegion.find(car => car.region === selectedRegion.value).countries;
    return allMarkets;
  };

  // Role options
  const roleOptions = rolesToDisplay.map(role => ({ label: role.role_name, value: role.role_id, ...role }));

  // Validation
  const validate = () => {
    const newErrors = {};
    if (!email) newErrors.email = "Email is required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) newErrors.email = "Invalid email address";
    if (parentDomain.length === 0) newErrors.parentDomain = "Parent Domain is required";
    if (domain.length === 0) newErrors.domain = "Domain is required";
    if (subDomain.length === 0) newErrors.subDomain = "Sub Domain is required";
    if (!cluster) newErrors.cluster = "Cluster is required";
    if (!market) newErrors.market = "Market is required";
    if (!role) newErrors.role = "Access is required";
    if (products.length === 0) newErrors.products = "Product is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const subDomainIds = subDomain.map(sub => sub.sub_domain_id);
    const productNames = products.map(p => p.product_name);
    createUser({
      user_email: email,
      role_id: role.value,
      country: market.label,
      region: cluster.label,
      cluster: cluster.label,
      created_by: localStorage.getItem("userId"),
      subdomain_ids: subDomainIds,
      product_names: productNames
    });
  };

  // React-select expects options as {label, value, ...}
  const parentDomainOptions = Array.isArray(parentDomainData?.parent_domains)
    ? parentDomainData.parent_domains.map(pd => ({ ...pd, label: pd.parent_domain, value: pd.parent_domain_id }))
    : [];
  const domainOptions = getAvailableDomains(parentDomain).map(d => ({ ...d, label: d.domain, value: d.domain_id }));
  const subDomainOptions = getAvailableSubdomains(domain).map(sd => ({ ...sd, label: sd.sub_domain, value: sd.sub_domain_id }));
  const marketOptions = getAvailableMarkets(cluster).map(country => ({ label: country, value: country }));
  const productOptions = productsData.filter(pd => subDomain.some(sd => sd.sub_domain_id === pd.sub_domain_id))
                        .map(pd => ({...pd, label: pd.product_name, value: pd.product_name }));

  // Reset domain/subdomain if parentDomain/domain changes
  useEffect(() => { setDomain([]); setSubDomain([]); }, [parentDomain]);
  useEffect(() => { setSubDomain([]); }, [domain]);
  useEffect(() => { setProducts([]); }, [subDomain]);

  // Reusable Select styles
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 45,
      border: '1px solid #ced4da',
      borderRadius: 8,
      boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 160, 0.1)' : 'none',
      borderColor: state.isFocused ? '#bb1ccc' : '#ced4da',
      '&:hover': {
        borderColor: '#bb1ccc'
      },
      width: '100%'
    }),
    menu: base => ({ 
      ...base, 
      zIndex: 9999,
      width: '100%'
    }),
    menuList: base => ({
      ...base,
      maxHeight: 200
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#bb1ccc' : state.isFocused ? '#f0f8ff' : 'white',
      color: state.isSelected ? 'white' : '#333',
      '&:hover': {
        backgroundColor: state.isSelected ? '#000080' : '#f0f8ff'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#f0f8ff',
      border: '1px solid #bb1ccc',
      borderRadius: 4
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#bb1ccc',
      fontWeight: 500,
      fontSize: '13px'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#bb1ccc',
      '&:hover': {
        backgroundColor: '#bb1ccc',
        color: 'white'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '8px 12px',
      width: '100%'
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0
    }),
    placeholder: (base) => ({
      ...base,
      color: '#999',
      fontSize: '14px'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#333',
      fontSize: '14px'
    })
  };

  return (
    <>
      <style>
        {`
          .input-container:focus-within {
            border-color: #bb1ccc !important;
            box-shadow: 0 0 0 1px #bb1ccc !important;
          }
        `}
      </style>
      <PopUpScreen header="Add Member" onHide={onHide} style={{ width: "40%" }}>
      <form onSubmit={handleSubmit}>
        <div className="grid">
          {/* Email */}
          <div className="col-12" style={{marginTop: "20px"}}>
            <LabelWrapper>
              <FieldLabel>Email</FieldLabel>
            </LabelWrapper>
            <div 
              className="input-container"
              style={{ 
                minHeight: 45, 
                border: '1px solid #ced4da', 
                borderRadius: 8, 
                display: 'flex', 
                alignItems: 'center', 
                paddingLeft: 12, 
                background: '#fff',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
              }}
            >
              <InputText
                placeholder="Enter your Email"
                id="email"
                name="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{ 
                  width: "100%", 
                  border: 'none', 
                  boxShadow: 'none', 
                  outline: 'none', 
                  background: 'transparent', 
                  height: 40, 
                  paddingLeft: 0,
                  fontSize: '14px',
                  color: '#333'
                }}
              />
            </div>
            {errors.email && <Message className="my-2 w-full" severity="warn" text={errors.email} />}
          </div>

          {/* Parent Domain */}
          <div className="col-12">
            <LabelWrapper>
              <FieldLabel>Parent Domain</FieldLabel>
            </LabelWrapper>
            <Select
              isMulti
              isLoading={isParentDomainLoading}
              isDisabled={isParentDomainLoading}
              options={parentDomainOptions}
              value={parentDomain}
              onChange={setParentDomain}
              placeholder={isParentDomainLoading ? "Loading..." : "Choose Parent Domains"}
              styles={selectStyles}
            />
            {errors.parentDomain && <Message className="my-2 w-full" severity="warn" text={errors.parentDomain} />}
          </div>

          {/* Domain */}
          <div className="col-12">
            <LabelWrapper>
              <FieldLabel>Domain</FieldLabel>
            </LabelWrapper>
            <Select
              isMulti
              isLoading={isParentDomainLoading}
              isDisabled={isParentDomainLoading || parentDomain.length === 0}
              options={domainOptions}
              value={domain}
              onChange={setDomain}
              placeholder={isParentDomainLoading ? "Waiting for Parent Domain..." : (parentDomain.length === 0 ? "Select Parent Domain(s) first" : "Choose Domains")}
              styles={selectStyles}
            />
            {errors.domain && <Message className="my-2 w-full" severity="warn" text={errors.domain} />}
          </div>

          {/* Sub Domain */}
          <div className="col-12">
            <LabelWrapper>
              <FieldLabel>Sub Domain</FieldLabel>
            </LabelWrapper>
            <Select
              isMulti
              isLoading={isParentDomainLoading}
              isDisabled={isParentDomainLoading || domain.length === 0}
              options={subDomainOptions}
              value={subDomain}
              onChange={setSubDomain}
              placeholder={isParentDomainLoading || domain.length === 0 ? "Select Domain(s) first" : "Choose Sub Domains"}
              styles={selectStyles}
            />
            {errors.subDomain && <Message className="my-2 w-full" severity="warn" text={errors.subDomain} />}
          </div>
        
           {/* Products */}
          <div className="col-12">
            <LabelWrapper>
              <FieldLabel>Product</FieldLabel>
            </LabelWrapper>
            <Select
              isMulti
              isLoading={isProductsLoading}
              isDisabled={isProductsLoading || subDomain.length === 0}
              options={productOptions}
              value={products}
              onChange={setProducts}
              placeholder={isProductsLoading || subDomain.length === 0? "Select Sub Domain(s) first" :  "Select Products"}
              styles={selectStyles}
            />
            {errors.products && <Message className="my-2 w-full" severity="warn" text={errors.products} />}
          </div>

          {/* Cluster */}
          <div className="col-12">
            <LabelWrapper>
              <FieldLabel>Region</FieldLabel>
            </LabelWrapper>
            <Select
              isLoading={isCountryAndRegionsLoading}
              isDisabled={isCountryAndRegionsLoading}
              options={clusterOptions}
              value={cluster}
              onChange={setCluster}
              placeholder={isCountryAndRegionsLoading ? "Loading..." : "Select Cluster"}
              styles={selectStyles}
            />
            {errors.cluster && <Message className="my-2 w-full" severity="warn" text={errors.cluster} />}
          </div>

          {/* Market */}
          <div className="col-12">
            <LabelWrapper>
              <FieldLabel>Market</FieldLabel>
            </LabelWrapper>
            <Select
              isLoading={isCountryAndRegionsLoading}
              isDisabled={isCountryAndRegionsLoading || !cluster}
              options={marketOptions}
              value={market}
              onChange={setMarket}
              placeholder={isCountryAndRegionsLoading ? "Loading..." : (!cluster ? "Select Cluster first" : "Select Market")}
              styles={selectStyles}
            />
            {errors.market && <Message className="my-2 w-full" severity="warn" text={errors.market} />}
          </div>

          {/* Role */}
          <div className="col-12">
            <LabelWrapper>
              <FieldLabel>Access</FieldLabel>
            </LabelWrapper>
            <Select
              isLoading={isAllRolesDataLoading}
              isDisabled={isAllRolesDataLoading}
              options={roleOptions}
              value={role}
              onChange={setRole}
              placeholder={isAllRolesDataLoading ? "Loading..." : "Choose the Access"}
              styles={selectStyles}
            />
            {errors.role && <Message className="my-2 w-full" severity="warn" text={errors.role} />}
          </div>
        </div>
        <div className="grid">
          <div className="col-12 mt-4">
            <Button
              label="Submit"
              type="submit"
              className="p-button-primary bg-[#bb1ccc] px-4 py-2 text-white"
              disabled={isUserCreating}
              loading={isUserCreating}
              style={{ float: "right" }}
            />
            <Button
              label="Cancel"
              className="px-4 py-2 border border-[#bb1ccc] text-[#bb1ccc] hover:bg-blue-50 transition"
              onClick={onHide}
              style={{ float: "right", marginRight: "10px" }}
            />
          </div>
        </div>
      </form>
    </PopUpScreen>
    </>
  );
}

export default AddNewMember;
